import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import {
  PREFILL_STATUS_OPTIONS,
  curatorErrorCopy,
  downloadCuratorExport,
  parseCuratorChildren,
  parseCuratorVenues,
  splitByAssignment,
  statusLabelOf,
  statusToneOf,
  useCuratorChildrenOffline,
  useCuratorVenues,
  type CuratorChild,
  type CuratorVenue,
} from '../../api/curator';
import { useCuratorGroups } from '../../api/curator-groups';
import { ApiError } from '../../api/client';
import { Button, EmptyState, ErrorState, Input, Spinner, Badge, Pagination, Select } from '../../components/ui';
import { TableWrap } from '../../components/TableWrap';
import { formatCount } from '../dashboard/format';
import {
  CABINET_PAGE_SIZE_OPTIONS,
  PREFILL_BUCKET_PARAM,
  PREFILL_BUCKETS,
  aggregateOffline,
  aggregateOnline,
  cabinetHeaderSub,
  cabinetPageSlice,
  cabinetRangeCaption,
  clampCabinetPage,
  groupFilterOptions,
  hasOfflineFilters,
  hasOfflineHalf,
  nextVenueView,
  normalizeCabinetPageSize,
  offlineProgramOptions,
  offlineRowsFiltered,
  parsePrefillBuckets,
  parseVenueSelection,
  prefillBucketsParam,
  prefillBucketCount,
  rowGroupLabel,
  togglePrefillBucket,
  venueChildCounts,
  venueViewArrangement,
  type OfflineFilters,
  type PrefillBucket,
  type VenueSelection,
  type VenueViewSelection,
} from './cabinet-model';
import { OfflineChildDrawer } from './OfflineChildDrawer';
import { PrefillOnlineBlock } from './PrefillOnlineBlock';
// ADDITIVE (v1.9.64, T.Z195) — the chase cells both children tables render.
import { ChaseCells, ChaseColumnHeaders } from './ChaseCells';
import { VenuePicker } from './VenuePicker';
import { useCabinetPageSize } from './useCabinetPageSize';
import './cabinet.css';

/**
 * «Кабинет куратора» — the eighth screen (T.Y8, contract #26–#26.6, SR v2.1 §5.2).
 *
 * The page is SCOPED, never global: `#26` answers the venues of the session's area
 * (`venue_managers ∪ venue_curators`) and every other cabinet read hangs off one of
 * them. The sections follow the venue (D6), not the user's label — since T.Z110 a
 * `REGISTRY` venue brings BOTH blocks, a `PARTNER_ONLINE` venue with the prefill flag
 * brings the online one alone. Contacts do not exist on any type the api
 * answers (A12/D9), so the tables cannot leak a phone number by accident.
 *
 * Status column (D5/D11): an unmatched row says «не найдено» plus WHAT to fix; a
 * matched row says the child's CURRENT system status — there is no «найдено» badge.
 *
 * T.Y18 (owner, live testing 2026-09-11): the picker gained «Все места» — the whole
 * scope in one screen. #26.2 answers the scope when `venueId` is absent, so the
 * online rows aggregate for free; the offline roster stays venue-scoped by contract
 * (#26.1 requires a `venueId`), so the aggregate view shows that half as numbers.
 * Rows open the EDIT drawer on click (the inline «Править»/«Удалить» are gone —
 * «Удалить» lives in the drawer, gated by the server's `canDelete`), the ФИО /
 * Номер заявления / УНТИ ID columns sort, and the export button asks for `xlsx`.
 *
 * T.Z14 (owner round 2, 2026-09-12): the summary chips became the table's filters
 * (C2/C6) — «Не найдено» now counts and shows the ambiguous rows too, and the word
 * «неоднозначно» left the UI; the partner-pool bar left the summary (C1 — the api
 * keeps sending it, nothing here reads it); the ФИО-format filter and badge left
 * (C3, the data stays on #27); the search box joined the filter row (C5, `?q=`,
 * v1.8.2); the picker options are venue names, bare (C10); and «Скачать xlsx» moved
 * into the online table's header, exporting exactly the chosen venue (Z5, `?venueId=`,
 * v1.8.2).
 *
 * T.Z50/T.Z51 (v1.9.5, 2026-09-15): «Группы» — the venue's study groups, managed here.
 * T.Z60 (owner, 2026-09-15) moves that section OUT: the management and the listing live
 * on the «Группы» page (`/groups`, {@link ./GroupsPage}) now, and the sidebar offers it
 * below «Педагоги». A group belongs to a ПЛОЩАДКА, so nothing about the block changed —
 * only the address did.
 *
 * T.Z48 (2026-09-15): a scope with NO offline venue renders NO offline surface
 * anywhere. The per-venue halves were already D6-driven (`summary.offline === null`
 * hides the roster block and the chips for that venue); what stayed behind were the
 * scope-level remnants — the aggregated «Все места» chips printed «План офлайн 0»
 * over a scope with no offline venue, and the header promised «офлайн-список — только
 * чтение» to a curator who has none. Both now key off {@link hasOfflineHalf} — the
 * fold over #26's own split, no new contract field.
 *
 * T.Z62/T.Z65 (v1.9.6, 2026-09-15): «Группа» reaches BOTH tables. The online table
 * filters by group SERVER-side (`?groupId=` on #26.2) and its drawer assigns/removes
 * through #26.12/#26.13 by the matched child's `applicationId`; the offline roster's
 * rows open a drawer with the child's identifiers and the same group select, and its
 * filter is client-side (the stated choice: #26.1 is one unpaginated read that already
 * carries the group). Every matched child now shows its ЕПГУ id and УНТИ id — the api
 * stands the matched application's identifiers in when the partner file carried none.
 *
 * T.Z83 (2026-09-16) — the OFFLINE table gains the online table's filter set, as far
 * as #26.1's projection can speak it: Статус, Поиск, Программа (the roster's own
 * stored captions — the projection publishes no `courseId` for the dictionary's
 * course-id currency) and the T.Z62 Группа. All client-side over the one read, all
 * component-local (no URL keys of its own), arranged like the online bar so the two
 * halves read as siblings.
 *
 * T.Z110 (owner, 2026-09-17): an OFFLINE venue's cabinet shows BOTH tables. The old
 * online gate — `summary.online !== null`, i.e. the prefill flag — hid the online
 * children table from every `REGISTRY` venue, while #26.2 itself has never filtered a
 * read by kind or flag (scope only): the gate was web-only, so the fix is. A venue
 * view now renders the online table for every offline venue ({@link venueShowsOnlineTable}),
 * rows or no rows — a venue with no children of a kind sees the honest empty state,
 * not a hint pointing at other venues. `canAdd` keeps reading `allowCuratorChildren`
 * and the api's writes keep refusing a flagged-off venue, so what T.Z110 widens is
 * what the curator SEES, never what they may write. Online venues are untouched.
 *
 * T.Z120 (owner, same day) REFINES that arrangement: the both-tables behaviour stays
 * only where the venue actually holds children of both kinds — and there the two new
 * count-widgets ({@link VenueViewSwitch}, «Заявления (офлайн)» / «Заявления (онлайн)»,
 * the counts from #26's own summaries, {@link venueChildCounts}) act as view switches.
 * A venue with children of one kind only renders that one table bare, no widgets; a
 * venue with no children at all keeps T.Z110's two honest empty states (the fresh
 * venue's «Добавить ребёнка» lives on the online one). Nothing about the writes moved.
 */
export function CabinetPage() {
  const [params, setParams] = useSearchParams();
  const venuesQuery = useCuratorVenues();
  const venues = useMemo(() => parseCuratorVenues(venuesQuery.data), [venuesQuery.data]);
  const sections = useMemo(() => splitByAssignment(venues), [venues]);
  // T.Z48 — the page's one offline gate: does ANY venue of the scope carry an
  // offline section? The api already answers it per venue on #26.
  const hasOffline = useMemo(() => hasOfflineHalf(venues), [venues]);

  // The selection the tables are scoped to, from the URL; absent = the first venue.
  const selection: VenueSelection | null = useMemo(
    () => parseVenueSelection(venues, params.get('venueId')),
    [venues, params],
  );
  const venue =
    selection !== null && selection.kind === 'venue'
      ? (venues.find((candidate) => candidate.venueId === selection.venueId) ?? null)
      : null;

  // T.Z120 — which of the two tables the venue view is narrowed to. Component-local on
  // purpose (the T.Z62/T.Z83 rule for the offline half's own state): a clicked state
  // lives until the other widget is clicked, the same one is clicked again (back to
  // both), or the venue changes — a fresh venue starts with BOTH tables, never with
  // the previous venue's narrowing. Not a URL key: the two tables already share one
  // address, and a view choice is not a linkable fact the way a filter is.
  const [view, setView] = useState<VenueViewSelection>(null);
  const selectedVenueId = selection?.kind === 'venue' ? selection.venueId : null;
  useEffect(() => setView(null), [selectedVenueId]);

  const setSelection = (raw: string) => {
    const next = new URLSearchParams(params);
    // An empty value (the picker's ×) lands on the page's own default: the first venue.
    const first = venues[0]?.venueId;
    const value = raw === '' ? (first === undefined ? null : String(first)) : raw;
    if (value === null) next.delete('venueId');
    else next.set('venueId', value);
    // A new scope means a new table: page 1 of a stale filter is not what was asked.
    next.delete('page');
    // T.Z62 — the «Группа» filter is venue-scoped (a group belongs to a ПЛОЩАДКА), so
    // a venue switch drops it: keeping the id would filter the new venue's table by
    // another venue's group and answer an honest-but-useless empty page.
    next.delete('groupId');
    setParams(next, { replace: true });
  };

  if (venuesQuery.isPending) {
    return (
      <div className="page-header">
        <h1>Кабинет куратора</h1>
        <Spinner label="Загружаем ваши площадки" />
      </div>
    );
  }

  if (venuesQuery.isError) {
    return (
      <>
        <PageHeader />
        <div className="card">
          <ErrorState
            message={copyOf(venuesQuery.error)}
            onRetry={() => void venuesQuery.refetch()}
          />
        </div>
      </>
    );
  }

  if (venues.length === 0) {
    return (
      // An empty scope has no offline venue — the sub line says so (T.Z48).
      <>
        <PageHeader hasOffline={false} />
        <div className="card">
          <EmptyState
            title="К вам пока не привязана площадка"
            hint="Кабинет показывает только закреплённые за вами площадки. Обратитесь к администратору, чтобы привязать площадку."
          />
        </div>
      </>
    );
  }

  if (selection === null) return null;

  // T.Z120 — the venue view's arrangement: which tables and whether the switch shows.
  // The «Все места» aggregate is not a venue view: no widgets there, both halves as
  // T.Y18 left them.
  const arrangement = venue !== null && selection.kind === 'venue' ? venueViewArrangement(venue, view) : null;

  return (
    <>
      <PageHeader hasOffline={hasOffline} />

      <div className="cabinet-toolbar">
        <VenuePicker venues={venues} selection={selection} onChange={setSelection} />
      </div>

      <SummaryBlock venues={venues} scope={selection.kind === 'all' ? venues : [venue!]} />

      {arrangement !== null && arrangement.widgets ? (
        <VenueViewSwitch venue={venue!} view={view} onSelect={setView} />
      ) : null}

      {arrangement !== null ? (
        <>
          {arrangement.offlineTable ? (
            <OfflineBlock venue={venue!} />
          ) : (
            <SectionHint
              // T.Z48's gate, kept: the pointer speaks only for a venue that has NO
              // offline half at all — a table narrowed away by the switch (or by the
              // single-kind rule) is here, just not asked for (T.Z120).
              shown={arrangement.offlineHint && hasOffline}
              text="Офлайн-список ведётся на других площадках вашей области — выберите одну из них в поле выше."
            />
          )}

          {arrangement.onlineTable ? (
            // T.Z110 — every OFFLINE venue brings the online table too (both tables, the
            // owner's «2 tables offline & online per offline venue»); T.Z120 narrows WHEN:
            // a venue with children of only one kind renders that one table, a both-kinds
            // venue keeps the both-tables default until a widget narrows it. The flag still
            // owns the create affordance, exactly as at an online venue.
            <PrefillOnlineBlock venueId={venue!.venueId} venueName={venue!.name} canAdd={venue!.allowCuratorChildren} />
          ) : (
            // What is left: a flagged-OFF online venue, whose set is led elsewhere —
            // the one case the pointer still describes. A switch hiding the online
            // table is not it (the same rule as the offline pointer above).
            <SectionHint
              shown={arrangement.onlineHint && sections.online.length > 0}
              text="Онлайн-набор ведётся на других площадках вашей области — выберите одну из них в поле выше."
            />
          )}
        </>
      ) : (
        // «Все места» — not a venue view, no switch: the aggregate keeps T.Y18's
        // arrangement, and a CREATE needs one venueId (it decides which partner pool
        // the row counts against, Р5), so the curator adds from a venue.
        <>
          <SectionHint
            shown={hasOffline}
            text="Офлайн-список ведётся по одной площадке — выберите её в поле выше, чтобы увидеть детей."
          />
          <PrefillOnlineBlock venueId={null} venueName="Все ваши площадки" canAdd={false} />
        </>
      )}
    </>
  );
}

/**
 * T.Z120 (owner, 2026-09-17) — the venue view's two count-widgets, «Заявления (офлайн)»
 * and «Заявления (онлайн)», as view switches. They render ONLY for a venue holding
 * children of both kinds ({@link venueHasBothKinds}) — a single-kind venue has nothing
 * to switch between and renders its one table bare.
 *
 * The grammar is the cabinet's own chip vocabulary (T.Z26's stat-tile chrome, the same
 * `aria-pressed` buttons the summary chips and the teachers chips speak): the count is
 * the whole set the click selects, the accent bar marks which table each tile is, and
 * the pressed state says which view is on. Clicking the pressed tile again releases it
 * — back to BOTH tables, T.Z110's arrangement — exactly the toggle the chips keep.
 */
function VenueViewSwitch({
  venue,
  view,
  onSelect,
}: {
  venue: CuratorVenue;
  view: VenueViewSelection;
  onSelect: (next: VenueViewSelection) => void;
}) {
  const counts = venueChildCounts(venue);
  const tiles = [
    { key: 'offline' as const, label: 'Заявления (офлайн)', accent: 'neutral', count: counts.offline },
    { key: 'online' as const, label: 'Заявления (онлайн)', accent: 'brand', count: counts.online },
  ];
  return (
    <div className="cabinet-view-switch" role="group" aria-label="Какие заявления показывать" data-testid="cabinet-view-switch">
      {tiles.map((tile) => {
        const active = view === tile.key;
        return (
          <button
            key={tile.key}
            type="button"
            className={['cabinet-chip', `cabinet-chip--${tile.accent}`, 'cabinet-chip--button', active ? 'cabinet-chip--active' : '']
              .filter(Boolean)
              .join(' ')}
            data-testid={`cabinet-view-${tile.key}`}
            aria-pressed={active}
            title={active ? 'Показать обе таблицы' : `Показать только: ${tile.label.toLowerCase()}`}
            onClick={() => onSelect(nextVenueView(view, tile.key))}
          >
            <span className="cabinet-chip__num">{formatCount(tile.count)}</span>
            <span className="cabinet-chip__label">{tile.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Why a section is missing: the scope HAS one, the chosen venue does not (D6 — the
 * sections follow the venue). Without it, «нет блока» reads as «нет данных».
 */
function SectionHint({ shown, text }: { shown: boolean; text: string }) {
  if (!shown) return null;
  return (
    <p className="card cabinet-section-hint" data-testid="cabinet-section-hint">
      {text}
    </p>
  );
}

/**
 * The sub line names the halves the scope actually has (T.Z48, {@link cabinetHeaderSub}) —
 * an online-only curator reads no «офлайн-список» promise the api would not back.
 * The default keeps the both-halves line for the one scope the page cannot describe:
 * a FAILED #26 read, where the halves are unknown and the error card owns the screen.
 */
function PageHeader({ hasOffline = true }: { hasOffline?: boolean }) {
  return (
    <div className="page-header">
      <div>
        <h1>Кабинет куратора</h1>
        <p className="page-header__sub" data-testid="cabinet-header-sub">
          {cabinetHeaderSub(hasOffline)}
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The venue picker lives in {@link ./VenuePicker} since T.Z60 — the «Группы» page
// reads the same control, so the two scopes cannot drift.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Summary (D6) — the chips ARE the table's filters since T.Z14/C2; T.Z26 restyles
// them as the dashboard's stat cards (the chrome only, never the behaviour)
// ---------------------------------------------------------------------------

/**
 * The accent each card takes, in the dashboard's own tone vocabulary (STYLEGUIDE §4):
 * the offline pair is context («План», «Назначено»), «Внесено» is the whole table,
 * «Найдено» is good news and «Не найдено» is the pile to work down — the one red on
 * the strip, and the reason it is worth being red.
 */
const CHIP_ACCENTS: Record<PrefillBucket, string> = {
  entered: 'brand',
  found: 'ok',
  notfound: 'bad',
};

/**
 * The numbers of the current scope: one venue, or the whole «Все места» scope
 * (summed venue by venue). The three online chips are buttons (C2): a click toggles
 * the bucket they count, and the working table below narrows to it — the chip's
 * number is the set the chip selects ({@link prefillRowMatchesBuckets}). The state
 * lives in the URL next to the other filters, so a shared link restores the view.
 *
 * The partner-pool bar is GONE (C1 — owner round 2): «использовано N из M» was
 * noise on a page that is mostly a table, and the pool's honest place is the
 * partner's own screen. The api still answers `partner` on #26; nothing here reads
 * it any more.
 *
 * T.Z26 restyles the strip as the dashboard's stat cards (card surface, the short
 * accent bar, the display-size number, the caption under it) — the chrome only:
 * same DOM, same testids, same `?bucket=` state, same toggle.
 *
 * T.Z48 — the offline pair keys off the SAME `null` in both scopes: a venue without
 * an offline section answers `summary.offline: null`, and an aggregated scope with no
 * offline venue at all answers `{@link aggregateOffline}`'s `null` — no «План офлайн 0»
 * over a scope that has no offline half.
 */
function SummaryBlock({ venues, scope }: { venues: readonly CuratorVenue[]; scope: readonly CuratorVenue[] }) {
  const [params, setParams] = useSearchParams();
  const buckets = parsePrefillBuckets(params.get(PREFILL_BUCKET_PARAM));
  const isWholeScope = scope.length === venues.length && venues.length > 1;
  const offline = isWholeScope ? aggregateOffline(scope) : scope[0]?.summary.offline ?? null;
  const online = isWholeScope ? aggregateOnline(scope) : scope[0]?.summary.online ?? null;

  const toggleBucket = (value: PrefillBucket) => {
    const next = new URLSearchParams(params);
    const updated = prefillBucketsParam(togglePrefillBucket(buckets, value));
    if (updated === null) next.delete(PREFILL_BUCKET_PARAM);
    else next.set(PREFILL_BUCKET_PARAM, updated);
    // The chips slice the LOADED page, so there is no stale page to reset — the
    // server filters keep their page. The URL is the only thing that moves.
    setParams(next, { replace: true });
  };

  return (
    <section className="card cabinet-summary" data-testid="cabinet-summary" aria-label="Сводка по площадке">
      <div className="cabinet-chips">
        {offline !== null ? (
          <>
            <span className="cabinet-chip cabinet-chip--neutral" data-testid="summary-offline-plan">
              <span className="cabinet-chip__num">{formatCount(offline.planOffline)}</span>
              <span className="cabinet-chip__label">План офлайн</span>
            </span>
            <span className="cabinet-chip cabinet-chip--brand" data-testid="summary-offline-assigned">
              <span className="cabinet-chip__num">
                {formatCount(offline.offlineAssigned)}
                <span className="cabinet-chip__share">из {formatCount(offline.planOffline)}</span>
              </span>
              <span className="cabinet-chip__label">Назначено офлайн</span>
            </span>
          </>
        ) : null}

        {online !== null
          ? PREFILL_BUCKETS.map((bucket) => {
              const active = buckets.includes(bucket.value);
              return (
                <button
                  key={bucket.value}
                  type="button"
                  className={[
                    'cabinet-chip',
                    `cabinet-chip--${CHIP_ACCENTS[bucket.value]}`,
                    'cabinet-chip--button',
                    active ? 'cabinet-chip--active' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  data-testid={`summary-${bucket.value}`}
                  aria-pressed={active}
                  title={active ? `Снять фильтр «${bucket.label}»` : `Показать: ${bucket.label.toLowerCase()}`}
                  onClick={() => toggleBucket(bucket.value)}
                >
                  <span className="cabinet-chip__num">
                    {formatCount(prefillBucketCount(bucket.value, online))}
                    {active ? <span className="cabinet-chip__share">●</span> : null}
                  </span>
                  <span className="cabinet-chip__label">{bucket.label}</span>
                </button>
              );
            })
          : null}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Offline roster (#26.1) — read-only rows, ONE editable fact: the учебная группа
// (T.Z62, v1.9.6). T.Z83 gives the table the SAME filter set the online one has,
// narrowed to what #26.1's projection can answer (status / search / programme /
// group; no «Аудитория» — the roster carries no course key to resolve it through).
// The filters are CLIENT-side by the stated T.Z62 choice: #26.1 is one unpaginated
// read that already carries each child's group and status, and the state lives in
// the component (not the URL) so it can never collide with the online table's own
// `?groupId=` filter on the same page — the two tables share one address.
//
// T.Z119 — the read STAYS one-shot (no contract change, the T.Z62 choice holds); the
// table pages CLIENT-side over the filtered rows, 20 a page by default, the same
// stored per-user page size the online table's selector writes. The page is
// component-local like the filters, and it resets whenever the venue or any filter
// changes — page 3 of a narrower roster is an empty screen nobody asked for.
// ---------------------------------------------------------------------------

function OfflineBlock({ venue }: { venue: CuratorVenue }) {
  const children = useCuratorChildrenOffline(venue.venueId);
  const groups = useCuratorGroups(venue.venueId);
  // T.Z83 — the filter state, component-local like the T.Z62 group filter it grew
  // out of. No debounce: narrowing is client-side over the loaded roster, so the
  // keystroke IS the query (the online box debounces because its search is a server
  // round-trip).
  const [filters, setFilters] = useState<OfflineFilters>({});
  const [child, setChild] = useState<CuratorChild | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // T.Z119 — the page and the per-user page size, the same pair the online table
  // keeps (its size rides in the URL, this one has no URL keys to spend). The hook is
  // the store BOTH blocks read, so the two selectors on one venue view never disagree.
  const [pageSize, choosePageSize] = useCabinetPageSize();
  const [pageState, setPageState] = useState(1);

  const all = useMemo(() => parseCuratorChildren(children.data), [children.data]);
  const rows = useMemo(() => offlineRowsFiltered(all, filters), [all, filters]);
  const programOptions = useMemo(() => offlineProgramOptions(all), [all]);
  const filtered = useMemo(() => hasOfflineFilters(filters), [filters]);

  // A venue or filter change restarts the table — the React-recommended render-time
  // reset, so the keystroke that narrows the roster also lands the reader on page 1
  // without an effect round-trip. Refetches of the SAME venue+filters leave the page.
  const [resetKey, setResetKey] = useState<string | null>(null);
  const pageKey = `${venue.venueId} ${JSON.stringify(filters)}`;
  if (pageKey !== resetKey) {
    setResetKey(pageKey);
    setPageState(1);
  }

  const page = clampCabinetPage(pageState, rows.length, pageSize);
  const pageRows = useMemo(() => cabinetPageSlice(rows, page, pageSize), [rows, page, pageSize]);

  const setFilter = <K extends keyof OfflineFilters>(key: K) => (value: OfflineFilters[K]) =>
    setFilters((current) => ({ ...current, [key]: value }));

  return (
    <section className="card card--flush cabinet-block" data-testid="cabinet-offline" aria-label="Офлайн-список">
      <header className="cabinet-block__head cabinet-block__head--row">
        <div>
          <h2>Офлайн (назначено)</h2>
          <p className="cabinet-block__sub">
            {venue.name} · только чтение, группу назначить можно. Контактов детей в кабинете нет — только статусы и
            идентификаторы.
          </p>
        </div>
        {/* T.Z47 (v1.8.9) — the same «download what the screen shows» the online half
            has: the file streams THIS venue's roster through #26.6 `?section=offline`. */}
        <OfflineExportButton venueId={venue.venueId} />
      </header>

      {/* ADDITIVE (v1.9.6, T.Z62) + (T.Z83) — the filter row, arranged like the online
          table's: Статус · Программа · Группа · Поиск · Строк · сброс. Same fields,
          same captions, same order the online bar keeps minus «Аудитория» (it needs a
          course key #26.1 does not publish). T.Z119 puts «Строк на странице» back —
          the roster pages client-side now, so the selector speaks here too. */}
      {all.length > 0 ? (
        <div className="filter-bar" role="search" aria-label="Фильтры офлайн-списка">
          <div className="filter-bar__row">
            <div className="filter-bar__field">
              <label className="filter-bar__label" htmlFor="offline-status">
                Статус
              </label>
              <Select
                id="offline-status"
                data-testid="offline-status"
                value={filters.status ?? ''}
                onChange={(event) => setFilter('status')(event.target.value === '' ? undefined : event.target.value)}
              >
                <option value="">Все статусы</option>
                {PREFILL_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="filter-bar__field">
              <label className="filter-bar__label" htmlFor="offline-program">
                Программа
              </label>
              {/* The roster's own captions, not the #21 dictionary: #26.1 publishes the
                  stored `programName` and no `courseId`, so the online filter's
                  course-id currency does not apply here. */}
              <Select
                id="offline-program"
                data-testid="offline-program"
                value={filters.programName ?? ''}
                onChange={(event) => setFilter('programName')(event.target.value === '' ? undefined : event.target.value)}
              >
                <option value="">Все программы</option>
                {programOptions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="filter-bar__field">
              <label className="filter-bar__label" htmlFor="offline-group">
                Группа
              </label>
              <Select
                id="offline-group"
                data-testid="offline-group"
                value={filters.groupId === undefined ? '' : String(filters.groupId)}
                onChange={(event) =>
                  setFilter('groupId')(event.target.value === '' ? undefined : event.target.value === 'none' ? 'none' : Number(event.target.value))
                }
              >
                {groupFilterOptions((groups.data ?? []).map((group) => ({ id: group.id, name: group.name }))).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="filter-bar__field filter-bar__field--search">
              <label className="filter-bar__label" htmlFor="offline-search">
                Поиск
              </label>
              <Input
                id="offline-search"
                data-testid="offline-search"
                type="search"
                value={filters.q ?? ''}
                placeholder="Поиск: ФИО, ЕПГУ id, УНТИ id"
                onChange={(event) => setFilter('q')(event.target.value)}
              />
            </div>
            {/* T.Z119 — the same selector the online bar keeps, the same stored choice:
                one preference, both tables. The slice itself is client-side over the
                one #26.1 read, so this is a view setting, not a query value. */}
            <div className="filter-bar__field">
              <label className="filter-bar__label" htmlFor="offline-pagesize">
                Строк на странице
              </label>
              <Select
                id="offline-pagesize"
                data-testid="offline-pagesize"
                value={String(pageSize)}
                onChange={(event) => {
                  choosePageSize(normalizeCabinetPageSize(event.target.value));
                  setPageState(1);
                }}
              >
                {CABINET_PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </Select>
            </div>
            {filtered ? (
              <Button size="sm" variant="ghost" data-testid="offline-reset" onClick={() => setFilters({})}>
                Сбросить фильтры
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      {notice !== null ? (
        <p className="cabinet-notice" role="status" data-testid="offline-notice">
          {notice}
        </p>
      ) : null}

      {children.isPending ? (
        <Spinner label="Загружаем список" />
      ) : children.isError ? (
        <ErrorState message={copyOf(children.error)} onRetry={() => void children.refetch()} />
      ) : (
        <>
          {filtered && all.length > rows.length ? (
            <p className="cabinet-notice" data-testid="offline-filter-note">
              Показаны дети выбранного фильтра: {rows.length} из {all.length}.
            </p>
          ) : null}
          <OfflineTable
            rows={pageRows}
            total={all.length}
            filtered={filtered}
            onOpen={(row) => {
              setNotice(null);
              setChild(row);
            }}
          />
          {/* T.Z119 — the pager speaks whenever the filtered roster has rows, one page
              included (the caption IS the count). It pages the TABLE only: the filters
              above it and the drawer keep working exactly as before. */}
          {rows.length > 0 ? (
            <Pagination
              meta={{
                page,
                totalPages: Math.max(1, Math.ceil(rows.length / Math.max(1, pageSize))),
                total: rows.length,
                limit: pageSize,
              }}
              // The roster note's own noun («дети»), the note's own idiom — the two
              // lines read as one sentence pair when a filter is on.
              rangeCaption={cabinetRangeCaption('дети', page, rows.length, pageSize)}
              onPage={setPageState}
            />
          ) : null}
        </>
      )}

      {child !== null ? (
        <OfflineChildDrawer
          venueId={venue.venueId}
          venueName={venue.name}
          child={child}
          onClose={() => setChild(null)}
          onSaved={(message) => {
            setChild(null);
            setNotice(message);
          }}
        />
      ) : null}
    </section>
  );
}

function OfflineTable({
  rows,
  total,
  filtered,
  onOpen,
}: {
  rows: ReturnType<typeof parseCuratorChildren>;
  /** The roster's whole size — the honest denominator of the filtered view. */
  total: number;
  filtered: boolean;
  onOpen: (row: CuratorChild) => void;
}) {
  if (rows.length === 0) {
    return filtered && total > 0 ? (
      <EmptyState
        title="Под фильтр детей не подошло"
        hint="В списке есть дети, но ни один не подходит под фильтры. Снимите фильтр над таблицей."
      />
    ) : (
      <EmptyState
        title="Офлайн-заявлений нет"
        hint="Как только площадка получит назначения, список появится здесь сам."
      />
    );
  }

  return (
    <TableWrap>
      <table className="data" data-testid="offline-table">
        <thead>
          <tr>
            <th scope="col">ФИО</th>
            <th scope="col">Регион</th>
            <th scope="col">Программа</th>
            <th scope="col">Статус</th>
            {/* ADDITIVE (v1.9.64, T.Z195) — the chase columns, right after the ЕВИ-stage
                chip: what the curator chases the child for (the contract, its deadline,
                the enrollment, the ЕВИ failure) — the register's own cells. */}
            <ChaseColumnHeaders />
            <th scope="col">Номер заявления</th>
            <th scope="col">УНТИ ID</th>
            {/* ADDITIVE (v1.9.6, T.Z62) — the учебная группа, from the row payload. */}
            <th scope="col">Группа</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={`${row.pguId}-${row.untiId ?? ''}`}
              data-testid="offline-row"
              className="cabinet-row--clickable"
              tabIndex={0}
              aria-label={row.fullName === null ? `Открыть ребёнка: номер заявления ${row.pguId}` : `Открыть ребёнка: ${row.fullName}`}
              onClick={() => onOpen(row)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onOpen(row);
                }
              }}
            >
              {/* T.Z139 — «—» is the honest cell: ФИО is ЕВИ-owned, so a child the
                  letter has not reached is a real row with no name yet (the api sends
                  no `fullName` key). Muted like the other empty cells. */}
              <td className={row.fullName === null ? 'cell-muted' : 'cell-primary'}>
                {row.fullName ?? '—'}
              </td>
              <td className={row.region === null ? 'cell-muted' : undefined}>{row.region ?? '—'}</td>
              <td className={row.programName === null ? 'cell-muted' : undefined}>{row.programName ?? '—'}</td>
              <td>
                <Badge tone={statusToneOf(row.currentStatus)} title={statusLabelOf(row.currentStatus)}>
                  {statusLabelOf(row.currentStatus)}
                </Badge>
              </td>
              {/* ADDITIVE (v1.9.64, T.Z195) — «Договор» · «Дедлайн договора» · «Зачислен» ·
                  «Не перешёл»: the api's chase facts, the register's cells. */}
              <ChaseCells row={row} id={row.applicationId ?? row.pguId} />
              <td>{row.pguId}</td>
              <td className={row.untiId === null ? 'cell-muted' : undefined}>{row.untiId ?? '—'}</td>
              <td className={row.group === null ? 'cell-muted' : undefined} data-testid={`offline-group-${row.applicationId ?? ''}`}>
                {rowGroupLabel(row.group)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableWrap>
  );
}

/**
 * «Скачать XLSX» (T.Z47, contract v1.8.9) — the offline roster's own download, the
 * twin of `PrefillOnlineBlock`'s export button and its same self-managed busy/error
 * pattern. It always pins the venue whose block it lives in (`?section=offline&venueId=`
 * — the api REQUIRES the id for this section), so the file is literally the table
 * above it: the same six columns, the same ФИО order, no contacts.
 */
function OfflineExportButton({ venueId }: { venueId: number }) {
  const [state, setState] = useState<'idle' | 'busy' | 'error'>('idle');
  const [message, setMessage] = useState<string | null>(null);

  const run = async () => {
    setState('busy');
    setMessage(null);
    try {
      await downloadCuratorExport('xlsx', venueId, 'offline');
      setState('idle');
    } catch (error) {
      setState('error');
      setMessage(error instanceof ApiError ? curatorErrorCopy(error) : 'Не удалось скачать файл.');
    }
  };

  return (
    <span className="export-button" data-testid="cabinet-offline-export-wrap">
      <Button
        variant="secondary"
        size="sm"
        data-testid="cabinet-offline-export"
        loading={state === 'busy'}
        onClick={() => void run()}
      >
        Скачать XLSX
      </Button>
      {message !== null ? (
        <span className="export-button__error" role="alert" data-testid="cabinet-offline-export-error">
          {message}
        </span>
      ) : null}
    </span>
  );
}

/**
 * The online half (T.Y18/T.Z14's `OnlineBlock`, its table and the export button)
 * moved out in T.Z27 — it is `{@link PrefillOnlineBlock}` now, so the venue card's
 * «Онлайн-набор» page can render the SAME block for a МЕНЕДЖЕР without a second
 * copy of the table. The cabinet keeps its own scope: the picker, the aggregate
 * and `venueId: null`.
 */

function copyOf(error: unknown): string {
  return curatorErrorCopy(error);
}
